import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const SEEN_KEY = "bl_intro_seen";
const MAX_MS = 15000; // hard stop once playing if the video never fires `ended`
const LOAD_MS = 6000; // if the film can't start within this, skip straight to the site

// Full-screen brand-reveal video shown once per session, then fades into the site.
export default function EmblemLoader({ onDone }) {
  const [visible, setVisible] = useState(() => !sessionStorage.getItem(SEEN_KEY));
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const doneRef = useRef(false);
  const playingRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    sessionStorage.setItem(SEEN_KEY, "1");
    setVisible(false);
    onDone?.();
  }, [onDone]);

  useEffect(() => {
    if (!visible) { onDone?.(); return; }
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    // Only start once enough is buffered to play through without stalling — no jerky first frames.
    const start = () => v.play().catch(finish); // autoplay blocked → go straight to the site
    if (v.readyState >= 4) start();
    else v.addEventListener("canplaythrough", start, { once: true });
    const loadGuard = setTimeout(() => { if (!playingRef.current) finish(); }, LOAD_MS);
    return () => { clearTimeout(loadGuard); v.removeEventListener("canplaythrough", start); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(finish, MAX_MS);
    return () => clearTimeout(t);
  }, [playing, finish]);

  // Footer "Replay brand film" re-opens the intro on demand.
  useEffect(() => {
    const replay = () => { doneRef.current = false; playingRef.current = false; setMuted(true); setPlaying(false); setVisible(true); };
    window.addEventListener("bl:replay-intro", replay);
    return () => window.removeEventListener("bl:replay-intro", replay);
  }, []);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="intro-loader"
          initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-hidden"
        >
          <video
            ref={videoRef}
            data-testid="intro-video"
            muted playsInline preload="auto"
            onPlaying={() => { playingRef.current = true; setPlaying(true); }}
            onEnded={finish}
            onError={finish}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${playing ? "opacity-100" : "opacity-0"}`}
          >
            <source src="/assets/reveal.mp4" type="video/mp4" />
          </video>

          {!playing && (
            <div data-testid="intro-loading" className="absolute inset-0 grid place-items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[color:var(--bl-blue)] animate-ping" />
            </div>
          )}

          <div className="absolute bottom-6 right-6 flex items-center gap-2 z-10">
            <button
              onClick={toggleSound}
              data-testid="intro-sound-toggle"
              aria-label={muted ? "Unmute intro" : "Mute intro"}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[color:var(--bl-blue)] bg-black/40 backdrop-blur-sm px-3 py-2 font-mono text-[10px] tracking-[0.28em] uppercase text-white/80 hover:text-[color:var(--bl-blue)] transition-colors"
            >
              {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
              {muted ? "Sound" : "Mute"}
            </button>
            <button
              onClick={finish}
              data-testid="intro-skip"
              className="border border-white/15 hover:border-[color:var(--bl-blue)] bg-black/40 backdrop-blur-sm px-3 py-2 font-mono text-[10px] tracking-[0.28em] uppercase text-white/80 hover:text-[color:var(--bl-blue)] transition-colors"
            >
              Skip Intro
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
